import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatInTimezone } from 'utils/datetime';

import { SEARCH_TYPE_LINKS } from 'consts/scenes';
import { DAY_IN_YEAR_FORMAT } from 'consts/datetime';
import { OBJECT_ID_TIMESTAMP_DELIMITER } from 'consts/bookmarks';
import { useDeleteBookmark, useEditBookmark } from 'services/bookmark';
import { useCurrentPage } from 'services/url';
import { useSavedLogViews } from 'services/search';
import { useSensorList } from 'services/sensor';
import darkTheme from 'services/theme/darkTheme';
import useStore from 'services/store';
import { useToggle } from 'utils/hooks';
import { updateQueryStringMultiple, parseQueryString } from 'utils/url';
import { saveSelectedPresetForPage } from 'utils/localStorage';
import { PAGES } from 'consts/pages';
import { isViewValid } from 'utils/viewValidation';
import { supportsSavedView, getViewPresetOptions } from 'utils/bookmarks';
import type { IdBookmark, PqlBookmark, Bookmark, BookmarkType } from 'services/bookmark';
import type { BookmarkFormValues } from 'components/common/BookmarkForm';

import Button from 'components/shared/Button';
import { ButtonsWrapper } from 'components/common/ButtonsWrapper';
import BookmarkForm from 'components/common/BookmarkForm';
import Badge from 'components/shared/Badge';
import { EditIcon, DeleteIcon, CloseIcon, AlertTriangleIcon } from 'components/common/Icons';
import { ItemContainer } from 'components/common/PageList';
import Spinner from '../Spinner';

/** Minimal type stub for the Zustand store selectors used in this component. */
type StoreState = {
  dashboardConfiguration: {
    applySavedView: (page: string, view: { name: string; widgets?: object[]; labels?: string[] }) => void;
    setSelectedPreset: (page: string, preset: string) => void;
    clearActiveSavedView: (page: string) => void;
  };
};

type BookmarkItemProps = {
  bookmark: Bookmark;
  type?: BookmarkType;
  openRecordName?: string | null;
  sidebar?: boolean;
  onEditingChange?: (editing: boolean) => void;
};

type NoBookmarksProps = {
  title: string;
  description?: string;
  sidebar?: boolean;
};

const SidebarDeleteButton = styled(Button)`
  position: absolute;
  top: 2px;
  right: 2px;
  padding: 0;
  width: 20px;
  height: 20px;
  min-width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  opacity: 0;
  transition: opacity 150ms ease-in-out;
  color: ${({ theme }) => theme.colors.text.subtle};

  svg {
    font-size: 12px;
  }

  &:hover {
    background: transparent;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Item = styled(ItemContainer)<{
  $sidebar?: boolean;
  $editingBookmark?: boolean;
  $isBroken?: boolean;
  $open?: boolean;
}>`
  color: ${({ $sidebar, theme }) => ($sidebar ? darkTheme.colors.text.primary : theme.colors.text.primary)};
  cursor: ${({ $editingBookmark, $isBroken }) => ($editingBookmark || $isBroken ? 'default' : 'pointer')};
  background: ${({ $sidebar, theme }) => ($sidebar ? 'rgba(50, 50, 50, 0.5)' : theme.colors.background.alt1)};
  position: relative;

  ${SidebarDeleteButton} {
    opacity: 0;
  }

  &:hover {
    ${SidebarDeleteButton} {
      opacity: 0.6;
    }
  }

  &:last-child {
    margin-bottom: ${({ $sidebar }) => ($sidebar ? 4 : 64)}px;
  }

  ${({ $isBroken, theme }) =>
    $isBroken &&
    css`
      border: 1px solid ${theme.palette.danger[2]};

      &:hover {
        border: 1px solid ${theme.palette.danger[2]};
      }

      ${SidebarDeleteButton} {
        opacity: 1;
      }
    `}

  ${({ $open, theme }) =>
    $open &&
    css`
      border: 1px solid ${theme.colors.primary};

      &:hover {
        border: 1px solid ${theme.colors.primary};
      }
    `}
`;

const NoResultsWrapper = styled(ItemContainer)<{ $sidebar?: boolean }>`
  flex-direction: column;
  align-items: flex-start;
  color: ${({ $sidebar, theme }) => ($sidebar ? darkTheme.colors.text.primary : theme.colors.text.primary)};
`;

const Labels = styled.div<{ $sidebar?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  min-height: 16px;
  color: ${({ theme }) => theme.colors.secondary};
  max-width: ${({ $sidebar }) => ($sidebar ? 100 : 95)}%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type SearchTypeLinkKey = keyof typeof SEARCH_TYPE_LINKS;

const getSearchTypeLink = (searchType: string): string | undefined =>
  searchType in SEARCH_TYPE_LINKS ? SEARCH_TYPE_LINKS[searchType as SearchTypeLinkKey] : undefined;

const getBookmarkHref = (location: ReturnType<typeof useLocation>, bookmark: Bookmark, type: BookmarkType) => {
  if (type === 'pql') {
    const pqlBookmark = bookmark as PqlBookmark;
    return updateQueryStringMultiple(
      { q: pqlBookmark.query, view: 'intel-events' },
      getSearchTypeLink(bookmark.searchType),
    );
  }

  const idBookmark = bookmark as IdBookmark;

  // Handle broken bookmarks with null objectId
  if (!idBookmark.objectId) {
    return null;
  }

  const DASHBOARD_PATHS = [
    '/alerting/overview',
    '/alerting/intel-events',
    '/investigation/indicators',
    '/investigation/logs',
  ];
  if (bookmark.searchType === 'O_ALERTS') {
    const queryParams: Record<string, string | undefined> = {
      r: undefined,
      rts: undefined,
      alert: idBookmark.objectId,
    };

    const isInDashboard = DASHBOARD_PATHS.some((p) => location.pathname.includes(p));
    const searchParams = new URLSearchParams(location.search);
    if (!isInDashboard) {
      queryParams.view = searchParams.get('view') === 'intel-events' ? 'intel-events' : 'all';
    }

    return updateQueryStringMultiple(queryParams, isInDashboard ? undefined : getSearchTypeLink(bookmark.searchType));
  }

  const queryParams: Record<string, string | undefined> = {
    r: idBookmark.objectId,
    rts: undefined,
    alert: undefined,
  };

  if (bookmark.searchType === 'O_SENSOR') {
    const [objectId, recordTs] = idBookmark.objectId.split(OBJECT_ID_TIMESTAMP_DELIMITER);
    queryParams.r = objectId;
    queryParams.rts = recordTs;
  }

  if (bookmark.searchType === 'O_EVENTS') {
    const [objectId, recordTs] = idBookmark.objectId.split(OBJECT_ID_TIMESTAMP_DELIMITER);
    queryParams.r = objectId;
    queryParams.rts = recordTs;
    queryParams.view = 'intel-events';
    return updateQueryStringMultiple(queryParams, getSearchTypeLink(bookmark.searchType));
  }

  return updateQueryStringMultiple(
    queryParams,
    DASHBOARD_PATHS.some((p) => location.pathname.includes(p)) ? undefined : getSearchTypeLink(bookmark.searchType),
  );
};

const toFormValues = (bookmark: Bookmark): Partial<BookmarkFormValues> => ({
  name: bookmark.name ?? '',
  description: bookmark.description ?? '',
  labels: bookmark.labels?.join(', ') ?? '',
  ...('query' in bookmark ? { query: (bookmark as PqlBookmark).query ?? '' } : {}),
  ...('view' in bookmark ? { view: bookmark.view ?? '' } : {}),
});

const BookmarkItem = React.memo(
  ({ bookmark, type = 'id', openRecordName = null, sidebar, onEditingChange }: BookmarkItemProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const page = useCurrentPage();
    const { list: savedViewsList } = useSavedLogViews();
    const { profilesList } = useSensorList();
    const applySavedView = useStore((state: StoreState) => state.dashboardConfiguration.applySavedView);
    const setSelectedPreset = useStore((state: StoreState) => state.dashboardConfiguration.setSelectedPreset);
    const clearActiveSavedView = useStore((state: StoreState) => state.dashboardConfiguration.clearActiveSavedView);

    const showView = type === 'pql' && supportsSavedView(bookmark.searchType);
    const presetOptions = useMemo(() => (showView ? getViewPresetOptions(profilesList) : []), [showView, profilesList]);
    const idBookmark = type === 'id' ? (bookmark as IdBookmark) : null;
    const bookmarkHref = getBookmarkHref(location, bookmark, type);
    const isBrokenBookmark = type !== 'pql' && !idBookmark?.objectId;
    const editingBookmark = useToggle();
    const deletingBookmark = useToggle();
    const deleteBookmark = useDeleteBookmark();
    const editBookmark = useEditBookmark();
    const queryParams = parseQueryString(location.search);

    const isOpen = openRecordName === (idBookmark?.objectId?.split(OBJECT_ID_TIMESTAMP_DELIMITER)?.[0] || '');

    const handleEditClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      editingBookmark.setOn();
      deletingBookmark.setOff();
      onEditingChange?.(true);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      editingBookmark.setOff();
      deletingBookmark.setOn();
      onEditingChange?.(false);
    };

    const handleEditBookmark = (values: BookmarkFormValues) => {
      editBookmark
        .mutateAsync({ values, bookmark, type })
        .then(() => {
          editingBookmark.setOff();
          onEditingChange?.(false);

          // Applying a view mutates the Log Search dashboard, so only do it while the user is there.
          if (
            showView &&
            page === PAGES.investigator &&
            values.view &&
            values.view !== (bookmark as PqlBookmark).view
          ) {
            const savedView = savedViewsList.data?.items?.find((view) => view.name === values.view);

            if (savedView?.name) {
              applySavedView(PAGES.investigator, {
                ...savedView,
                name: savedView.name,
                widgets: savedView.widgets ?? undefined,
                labels: savedView.labels ?? undefined,
              });
            } else if (values.view) {
              clearActiveSavedView(PAGES.investigator);
              setSelectedPreset(PAGES.investigator, values.view);
              saveSelectedPresetForPage(PAGES.investigator, values.view);
            }
          }
        })
        .catch(() => {});
    };

    const handleCloseEdit = () => {
      editingBookmark.setOff();
      onEditingChange?.(false);
    };

    const handleDeleteBookmark = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      deleteBookmark
        .mutateAsync({ bookmark, type })
        .then(() => {
          deletingBookmark.setOff();
          onEditingChange?.(false);
        })
        .catch(() => {});
    };

    const handleBookmarkItemClick = () => {
      // Don't navigate if bookmark is broken
      if (!bookmarkHref) {
        return;
      }

      const [pathname, query] = bookmarkHref.split('?');

      navigate(`${pathname}${query ? `?${query}` : ''}`, {
        state: {
          previousRecordName: queryParams.r || queryParams.alert,
        },
      });

      if (showView && (bookmark as PqlBookmark).view) {
        const pqlView = (bookmark as PqlBookmark).view;
        const savedView = savedViewsList.data?.items?.find((view) => view.name === pqlView);

        if (savedView?.name) {
          applySavedView(PAGES.investigator, {
            ...savedView,
            name: savedView.name,
            widgets: savedView.widgets ?? undefined,
            labels: savedView.labels ?? undefined,
          });
        } else if (pqlView) {
          clearActiveSavedView(PAGES.investigator);
          setSelectedPreset(PAGES.investigator, pqlView);
          saveSelectedPresetForPage(PAGES.investigator, pqlView);
        }
      }
    };

    return (
      <Item
        $sidebar={sidebar}
        onClick={handleBookmarkItemClick}
        $editingBookmark={editingBookmark.isOn || deletingBookmark.isOn}
        $isBroken={isBrokenBookmark}
        $open={isOpen}
        data-test-id={`bookmark-item-${bookmark.name}`}
      >
        <div className="flex flex-col w-full">
          <div className="flex overflow-hidden">
            <div className="flex-1 overflow-hidden">
              <div className="text-base font-semibold whitespace-nowrap max-w-[95%] overflow-hidden text-ellipsis">
                {bookmark.name}
              </div>
              {bookmark.description && (
                <div className="text-sm min-h-4 max-w-[95%] overflow-hidden text-ellipsis">{bookmark.description}</div>
              )}
              {isBrokenBookmark && (
                <Badge variant="solid" color="red" size="1" className="mt-1">
                  <AlertTriangleIcon aria-hidden className="size-3" />
                  Broken bookmark
                </Badge>
              )}
              {!isBrokenBookmark && bookmark.labels && bookmark.labels.length > 0 && (
                <Labels $sidebar={sidebar}>{bookmark.labels.join(', ')}</Labels>
              )}
              {showView && isViewValid((bookmark as PqlBookmark).view, savedViewsList.data?.items, presetOptions) && (
                <div className="text-sm max-w-[95%] overflow-hidden text-ellipsis">
                  {(bookmark as PqlBookmark).view}
                </div>
              )}
            </div>

            {sidebar && !deletingBookmark.isOn && !editingBookmark.isOn && (
              <SidebarDeleteButton variant="ghost" onClick={handleDeleteClick} aria-label="Remove bookmark">
                <CloseIcon />
              </SidebarDeleteButton>
            )}

            {!sidebar && (
              <>
                {bookmark.createdAt && (
                  <div className="mr-2 flex-1 max-w-25 flex flex-col justify-center">
                    <div className="old-text-xs">Created at</div>
                    <div className="old-text-sm color-text-subtle">
                      {formatInTimezone(bookmark.createdAt, DAY_IN_YEAR_FORMAT)}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Button size="2" className="m-0!" variant="ghost" color="gray" onClick={handleEditClick}>
                    <EditIcon />
                  </Button>

                  <Button size="2" className="m-0!" variant="ghost" color="red" onClick={handleDeleteClick}>
                    <DeleteIcon />
                  </Button>
                </div>
              </>
            )}
          </div>

          {editingBookmark.isOn && (
            <BookmarkForm
              type={type}
              bookmark={toFormValues(bookmark)}
              searchType={bookmark.searchType}
              error={editBookmark.error}
              loading={editBookmark.isPending}
              onClose={handleCloseEdit}
              onSubmit={handleEditBookmark}
            />
          )}

          {deletingBookmark.isOn && (
            <div className="flex flex-col py-1 mt-1 max-h-25">
              Are you sure you want to delete this bookmark?
              <ButtonsWrapper className="mt-2 justify-end items-center">
                <Button
                  size="1"
                  variant="ghost"
                  className="text-white-1!"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    e.preventDefault();
                    deletingBookmark.setOff();
                    onEditingChange?.(false);
                  }}
                >
                  Cancel
                </Button>

                <Button
                  size="1"
                  color="red"
                  variant="solid"
                  onClick={deleteBookmark.isPending ? () => {} : handleDeleteBookmark}
                >
                  Delete
                  {deleteBookmark.isPending && <Spinner />}
                </Button>
              </ButtonsWrapper>
            </div>
          )}
        </div>
      </Item>
    );
  },
);

export const NoBookmarks = ({ title, description, sidebar }: NoBookmarksProps) => {
  return (
    <NoResultsWrapper $sidebar={sidebar} onClick={undefined}>
      <div className="text-base font-semibold whitespace-nowrap max-w-[95%] overflow-hidden text-ellipsis">{title}</div>
      {description && <div className="text-sm min-h-4 max-w-[95%] overflow-hidden text-ellipsis">{description}</div>}
    </NoResultsWrapper>
  );
};

export default BookmarkItem;
